import Link from "next/link";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { global_config } from "src/shared/global_config";
import { routes } from "src/shared/routes";
import { TRegistrationCardData } from "src/shared/types/globalStotre.types";
import { TTeamConfig } from "src/shared/types/teamConfig.types";
import { openPdfInNewTab } from "src/utils/utils";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Purchase Post Season D-backs Tickets",
    description: (
      <>
        Purchase your employee post-season D-backs tickets here. Tickets are
        non-refundable. Please use your{" "}
        <Link href={"https://dbacks.com"} target="_blank">
          dbacks.com
        </Link>{" "}
        employee email to purchase your tickets.
      </>
    ),
    authRedirectPage: routes.home,
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "You will receive a text when the window to opt in is opened. The opt-in window starts at 9am on game day. After opting in you will have the opportunity to purchase guest tickets & upgrades if they are available.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Tickets are guaranteed for the three predetermined games in the subscription. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket. Please note that your Hat trick Subscription Pass only guarantees you tickets to 3 specific games a month. You can review your monthly schedule on your homepage.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "You will be notified tickets are available 3 hours prior to game start and are delivered within your Ticketmaster Account Manager or the St. Louis Blues app.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "3 hours prior to game start you will receive a text confirming that tickets have been delivered to Ticketmaster Account Manager and the St. Louis Blues app.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seats will be located in the Mezzanine Level (upper bowl) around the arena.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Hat Trick Subscription Pass tickets, as well as seat upgrades and guest passes, are not eligible for resale or transfer. Abusing these guidelines can result in your Hat Trick Subscription Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please contact customerservice@stlblues.com for any assistance you may require.",
  },

  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        You can absolutely create a group to link with other pass holders!
        Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "stlouisblues_pass" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        next to or close enough to hand a drink to one another for the game. You
        will need to ensure your group is created and all members have joined at
        least 4 hours prior to game start in order to be seated with one
        another.
      </>
    ),
  },

  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets & upgrades will be available for select games. If available, guest tickets are available for purchase when you opt into games.",
  },

  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        An active purchase for the month is not refundable. To cancel your pass
        for the upcoming month opt-out through the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "stlouisblues_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab.
      </>
    ),
  },

  {
    question:
      "How can I change my payment method or update my expiration date?",
    answer: (
      <>
        To update your payment method, simply navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "stlouisblues_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        menu on the right side of your screen. You will see a section to update
        your payment method where you can update new information.
      </>
    ),
  },

  {
    question: "How do I renew my subscription?",
    answer: (
      <>
        Your subscription will automatically renew on the 26th of the month!
        There is no action needed on your end to keep your subscription active.
      </>
    ),
  },

  {
    question: "Can I pause my subscription?",
    answer: (
      <>
        Yes, once you navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "stlouisblues_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab in your account you can elect to pause your subscription for that
        month during the opt out window from the 15th to the 26th. You will
        receive a text message when this period is open.{" "}
      </>
    ),
  },

  {
    question: "Can I cancel my subscription?",
    answer: (
      <>
        Simply navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "stlouisblues_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab to cancel your subscription. You can only do so during the monthly
        opt out period from the 15th to the 26th of the month.{" "}
      </>
    ),
  },

  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },

  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        An active purchase for the month is not refundable. To cancel your pass
        for the upcoming month opt-out through the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "stlouisblues_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        tab.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => {
  const theme = useSSTheme();
  const { md: mdUp } = useCssMediaQuery("up");
  const router = useRouterExt();
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from{" "}
      <Link href={"https://dbacks.com"} target="_blank">
        dbacks.com
      </Link>
      . You may unsubscribe at any time. By checking this box you agree to
      receive additional commercial emails from{" "}
      <Link href={"https://dbacks.com"} target="_blank">
        dbacks.com
      </Link>{" "}
      and{" "}
      <Link href={"https://mlb.com"} target="_blank">
        MLB.com
      </Link>
      . The terms and conditions of your ticket and policies applicable to its
      use are updated to address circumstances related to COVID-19 and health
      and safety. By clicking the Purchase Tickets button, you agree to accept
      all{" "}
      <Link
        href={`${window.location.origin}/dbacks_postseason/dbacks_terms_and_condition`}
        target="_blank"
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
        }
        target="_blank"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
        }
        target="_blank"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Diamondbacks account. Message and data rates may
      apply Tickets will be made available in the MLB Ballpark mobile
      application only.{" "}
      <Link href={"https://mlb.com"} target="_blank">
        MLB.com
      </Link>{" "}
      and supported mobile device required.
    </>
  );
};
